/**
 * eMahkamah Perusahaan Malaysia (eMP) v2.0
 * Main JavaScript Application File
 */

require('./bootstrap');

// Import Alpine.js for reactive components
import Alpine from 'alpinejs';
window.Alpine = Alpine;
Alpine.start();

// Import Chart.js for data visualization
import Chart from 'chart.js/auto';
window.Chart = Chart;

// Flatpickr date picker
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('input[type="date"]').forEach(el => {
        el.type = 'text';
        flatpickr(el, {
            dateFormat: 'd/m/Y',
            altInput: true,
            altFormat: 'd/m/Y',
            allowInput: true,
        });
    });

    document.querySelectorAll('input[type="datetime-local"]').forEach(el => {
        el.type = 'text';
        flatpickr(el, {
            enableTime: true,
            dateFormat: 'd/m/Y H:i',
            altInput: true,
            altFormat: 'd/m/Y H:i',
            allowInput: true,
        });
    });
});

// Global EMP Application Object
window.EMP = {
    // Configuration
    config: {
        appName: 'eMahkamah Perusahaan Malaysia',
        version: 'v2.0',
        apiUrl: '/api/v1',
    },

    // Utility Functions
    utils: {
        /**
         * Format currency to Malaysian Ringgit
         */
        formatCurrency: function(amount) {
            return 'RM ' + parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        },

        /**
         * Format date to Malaysian format (DD/MM/YYYY)
         */
        formatDate: function(dateString) {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        },

        /**
         * Format datetime to Malaysian format with time
         */
        formatDateTime: function(dateString) {
            const date = new Date(dateString);
            const formattedDate = this.formatDate(dateString);
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${formattedDate} ${hours}:${minutes}`;
        },

        /**
         * Calculate days between two dates
         */
        daysBetween: function(date1, date2) {
            const oneDay = 24 * 60 * 60 * 1000;
            const firstDate = new Date(date1);
            const secondDate = new Date(date2);
            return Math.round(Math.abs((firstDate - secondDate) / oneDay));
        },

        /**
         * Show toast notification
         */
        showToast: function(message, type = 'success') {
            // Implementation would use Alpine.js component
            console.log(`[${type.toUpperCase()}] ${message}`);

            // Dispatch custom event for Alpine components
            window.dispatchEvent(new CustomEvent('show-toast', {
                detail: { message, type }
            }));
        },

        /**
         * Confirm action dialog
         */
        confirm: function(message, callback) {
            if (window.confirm(message)) {
                callback();
            }
        },
    },

    // Chart Helpers
    charts: {
        /**
         * Default chart colors matching EMP theme
         */
        colors: {
            primary: '#1E3A8A',
            secondary: '#0EA5E9',
            accent: '#F59E0B',
            success: '#10B981',
            danger: '#EF4444',
            warning: '#F97316',
        },

        /**
         * Create a line chart for case trends
         */
        createTrendChart: function(canvasId, labels, data, label = 'Cases') {
            const ctx = document.getElementById(canvasId);
            if (!ctx) return null;

            return new Chart(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [{
                        label: label,
                        data: data,
                        borderColor: this.colors.primary,
                        backgroundColor: this.colors.primary + '20',
                        tension: 0.4,
                        fill: true,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'bottom',
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                precision: 0
                            }
                        }
                    }
                }
            });
        },

        /**
         * Create a bar chart for comparisons
         */
        createBarChart: function(canvasId, labels, data, label = 'Count') {
            const ctx = document.getElementById(canvasId);
            if (!ctx) return null;

            return new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [{
                        label: label,
                        data: data,
                        backgroundColor: this.colors.secondary,
                        borderColor: this.colors.primary,
                        borderWidth: 1,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: false,
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                precision: 0
                            }
                        }
                    }
                }
            });
        },

        /**
         * Create a donut chart for distributions
         */
        createDonutChart: function(canvasId, labels, data) {
            const ctx = document.getElementById(canvasId);
            if (!ctx) return null;

            return new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: labels,
                    datasets: [{
                        data: data,
                        backgroundColor: [
                            this.colors.primary,
                            this.colors.secondary,
                            this.colors.accent,
                            this.colors.success,
                            this.colors.warning,
                            this.colors.danger,
                        ],
                        borderWidth: 2,
                        borderColor: '#ffffff',
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'right',
                        }
                    }
                }
            });
        },
    },

    // API Client
    api: {
        /**
         * Make GET request
         */
        get: async function(endpoint) {
            try {
                const response = await fetch(`${window.EMP.config.apiUrl}${endpoint}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                    },
                });
                return await response.json();
            } catch (error) {
                console.error('API GET Error:', error);
                throw error;
            }
        },

        /**
         * Make POST request
         */
        post: async function(endpoint, data) {
            try {
                const response = await fetch(`${window.EMP.config.apiUrl}${endpoint}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                    },
                    body: JSON.stringify(data),
                });
                return await response.json();
            } catch (error) {
                console.error('API POST Error:', error);
                throw error;
            }
        },
    },
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
    console.log(`%c${window.EMP.config.appName} ${window.EMP.config.version}`, 'color: #1E3A8A; font-size: 16px; font-weight: bold;');
    console.log('%cKementerian Sumber Manusia', 'color: #F59E0B; font-size: 12px;');
});
